
import Vue from 'vue';

import Autocomplete from '@/components/Autocomplete.vue';
import * as CorpusStore from '@/store/search/corpus';
import * as ConceptStore from '@/store/search/form/conceptStore';
import { mapReduce, uniq } from '@/utils'

import SelectPicker from '@/components/SelectPicker.vue';
import { conceptApi, blacklab } from '@/api';

type Term = { term: string }

export default Vue.extend ( {
	name: 'ConceptSearchBox',
	components: { Autocomplete, SelectPicker },
	props: {
		id: String,
		field: String,
		settings: Object as () => ConceptStore.Settings,
		value: Array as () => Array<{field: string, value: string}>
	},
	data: () => ({
		/** A "field" of study, like "history", "philosophy", etc. */
		search_field: '',
		/** A "concept" within the "field", like the concept of "truth and falsehood" in the field of "philosophy" */
		current_concept : '',
		/** A world that falls within the concept, like "true", "false" */
		current_term : '',
		/** Which terms we're currently using. */
		checked_terms: {} as Record<string, boolean>,
		/** All available terms. */
		terms: [] as string[],

		WITH_CREDENTIALS,
	}),

	methods : {
		getConceptAutocompletes(prefix: string): Promise<string[]> {
			if (!prefix) return Promise.resolve([]);

			return conceptApi.getTerms(this.settings.instance, this.search_field, prefix)
		},
		/** Autocomplete. Find all terms starting with the string. Search both the database and Blacklab. */
		getTermAutocompletes(prefix: string): Promise<string[]> {
			if (!prefix) return Promise.resolve([]);

			const mainAnnotation = CorpusStore.get.firstMainAnnotation();
			// TODO use api module.
			return Promise.all([
				conceptApi.getTerms(this.settings.instance, this.search_field, this.current_concept, prefix),
				blacklab.getTermAutocomplete(INDEX_ID, mainAnnotation.annotatedFieldId, 'lemma', prefix)
			])
			.then(([pdb, corpus]) => uniq([...pdb, ...corpus]))
		},

		get_cluster_values(d: { data: ConceptStore.LexiconEntry[] }): string[] {
			const vals = uniq(d.data.map(x  => x.cluster))
			return vals
		},
		insertConcept() {
			if (!this.current_concept) return;
			this.addToDatabase(this.current_concept);
		},
		insertTerm() {
			if (!this.current_term || this.checked_terms[this.current_term]) return;
			this.$set(this.checked_terms, this.current_term, true);
			this.addToDatabase(this.current_concept, this.current_term);
		},
		addToDatabase(concept: string, term?: string) {
			if (!this.settings) return;
			conceptApi.addConceptOrTermToDatabase(
				this.settings.instance,
				INDEX_ID,
				this.search_field,
				concept,
				term
			)
			.then(() => console.log('inserted term'), console.error);
		},
		resetQuery() {
			this.current_concept = ''
			this.current_term = ''
			this.terms = []
			this.checked_terms = {}
		}
	},

	computed : {
		main_fields: ConceptStore.get.main_fields,
		fieldAndConcept(): {field: string, concept: string} {
			return {field: this.search_field, concept: this.current_concept}
		},
		newValue(): ConceptStore.AtomicQuery[] {
			return Object
			.keys(this.checked_terms)
			.filter(t => this.checked_terms[t])
			.map(t => ({field: 'lemma', value: t})); // TODO don't hardcode lemma!.
		},
	},
	watch: {
		/** Get the terms for the current field+concept from the database */
		fieldAndConcept(): void {
			const {field, concept} = this.fieldAndConcept;
			conceptApi
				.getTerms(this.settings.instance, field, concept)
				.then(terms => {
					this.terms = terms;
					this.checked_terms = mapReduce(terms); // check all terms.
				})
				.catch(e => console.error(`Failed to retrieve terms for concept ${concept} in field ${field}`, e));
		},
		// Update selected field when main fields change.
		// can be removed when we update selectpicker to the component library/vue 3 version
		// as v-model autocorrects the value in that version.
		main_fields(n: string[]) {
			if (n && n.length > 0)
				this.search_field = n[0]
		},
		// v-model helper.
		newValue() {
			// don't remove the type cast, so we get a warning here if we change something.
			this.$emit('input', this.newValue as ConceptStore.AtomicQuery[]);
		},
	},
})
